import type { ChangeEvent, FocusEvent, ReactNode } from 'react';
import type {
  Interpolation,
  PropsOf,
  Theme,
  SerializedStyles,
} from '@emotion/react';
import type { IconName } from '@components/Icon/BaseIcon';
import { useRef, useImperativeHandle, forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import * as tokens from '@tokens';
import { Icon } from '@components/Icon/Icon';

import { baseInputGhost, baseInputGhostContainer } from './styles';

export interface BaseInputProps
  extends Omit<
    PropsOf<'input'>,
    'size' | 'required' | 'readOnly' | 'disabled'
  > {
  /**
   * The unique ID (attribute) of the html input element.
   */
  id?: string;
  /**
   * Name (attribute) of the html input element.
   */
  name?: string;
  /**
   * Placeholder for the input element.
   */
  placeholder?: string;
  /**
   * Default value for the input element.
   */
  defaultValue?: string;
  /**
   * Size of the input. 3 sizes, to result in 32/40/56 px height.
   */
  size?: 's' | 'm' | 'l';
  /**
   * Icon to show on the left of the input.
   */
  icon?: IconName;
  /**
   * Specifies if the input is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies if the input is invalid. When set to true, we'll render the input in invalid state.
   */
  isInvalid?: boolean;
  /**
   * Specifies if the error icon should be rendered when the input is invalid.
   */
  showInvalidIcon?: boolean;
  /**
   * Specifies if the input is required.
   */
  isRequired?: boolean;
  /**
   * Specifies if the input is in readonly.
   */
  isReadonly?: boolean;
  /**
   * A way to render something on the right of the input. Size available is a 16/16 square.
   */
  actionContent?: ReactNode;
  /**
   * A way to render anything. Will be inside the input container.
   */
  customContent?: ReactNode;
  /**
   * Callback when the input value is changed.
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Callback when the input is focused.
   */
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  /**
   * Callback when the input looses focus.
   */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /**
   * Styles override for the input element
   */
  inputStyles?: Interpolation<Theme>;
  /**
   * Styles override for the input container
   */
  className?: string;
}

/**
 * Props type for specific input implementations with no custom logic,
 * e.g. `<input type="text" />`
 */
export type SpecificInputProps = Omit<
  BaseInputProps,
  'customContent' | 'showInvalidIcon' | 'type' | 'ref'
>;

const INPUT_HEIGHT = {
  s: 32,
  m: 40,
  l: 56,
};

interface InputContainerProps {
  isInvalid: boolean;
  isDisabled: boolean;
  isReadonly: boolean;
  isActionPresent?: boolean;
}

const Wrapper = styled.div`
  position: relative;
`;

/**
 * **WARNING!** _We're exporting those styles in case Insights
 * might need to mimic input styles on non standard components._
 * Do not use it, unless it's absolutely unnecessary.
 *
 * @Summary Base styles for Input Container component
 */
export function getInputContainerStyles(
  props: InputContainerProps,
): SerializedStyles {
  const { isActionPresent } = props;

  return css`
    display: flex;
    align-items: center;
    padding-right: ${isActionPresent ? tokens.spacer.x4 : ''};
    ${baseInputGhostContainer(props)}
  `;
}

const InputContainer = styled.div<InputContainerProps>`
  ${(props) => getInputContainerStyles(props)}
`;

const InputIcon = styled(Icon)`
  margin: auto;
  flex: 0 0 auto;
`;

const ActionContentContainer = styled.div`
  height: ${tokens.spacer.x2};
  width: ${tokens.spacer.x2};
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto;
  right: ${tokens.spacer.x2};
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface StyledInputProps {
  inputSize: 's' | 'm' | 'l';
  isInvalid: boolean;
  readOnly: boolean;
  isIconPresent: boolean;
}

/**
 * **WARNING!** _We're exporting those styles in case Insights
 * might need to mimic input styles on non standard components._
 * Do not use it, unless it's absolutely unnecessary.
 *
 * @Summary Base styles for Input component
 */
export function getInputStyles(props: StyledInputProps): SerializedStyles {
  const { inputSize, isInvalid, isIconPresent } = props;

  return css`
    height: ${INPUT_HEIGHT[inputSize]}px;
    padding: 0 ${isIconPresent ? tokens.spacer.x1 : tokens.spacer.x2} 0
      ${tokens.spacer.x2};
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    ${tokens.transition()};
    ${baseInputGhost(isInvalid)}
  `;
}

const StyledInput = styled.input<StyledInputProps>`
  ${(props) => getInputStyles(props)}
`;

/**
 * BaseInput component.
 *
 * **WARNING!** _This component is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 * @summary Provides a base component for all input components
 * @see TextInput
 */
export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      isDisabled = false,
      isInvalid = false,
      isRequired = false,
      isReadonly = false,
      icon,
      actionContent,
      customContent,
      size = 'm',
      className,
      inputStyles,
      showInvalidIcon = true,
      ...props
    }: BaseInputProps,
    ref,
  ): JSX.Element => {
    const inputRef = useRef();
    const isActionPresent = !!actionContent;
    const isValidationIconPresent = isInvalid && showInvalidIcon;
    const onContainerClick = () => {
      const input = inputRef?.current as HTMLInputElement;
      if (input && input !== document.activeElement) {
        input.focus();
      }
    };

    useImperativeHandle(ref, () => inputRef.current);

    return (
      <Wrapper>
        <InputContainer
          onClick={onContainerClick}
          className={className}
          isInvalid={isInvalid}
          isDisabled={isDisabled}
          isReadonly={isReadonly}
          isActionPresent={isActionPresent}
        >
          {!!icon && (
            <InputIcon
              icon={icon}
              size="m"
              color="text_disabled"
              css={css`
                margin-right: -${tokens.spacer.x1};
                margin-left: ${tokens.spacer.x2};
              `}
            />
          )}
          <StyledInput
            ref={inputRef}
            {...props}
            inputSize={size}
            isInvalid={isInvalid}
            required={isRequired}
            disabled={isDisabled}
            readOnly={isReadonly}
            isIconPresent={isValidationIconPresent || isActionPresent}
            css={inputStyles}
          />
          {isValidationIconPresent && (
            <InputIcon
              icon="warning_alt"
              size="m"
              color="text_danger"
              css={css`
                margin-right: ${isActionPresent
                  ? tokens.spacer.x1
                  : tokens.spacer.x2};
              `}
            />
          )}
        </InputContainer>
        {isActionPresent && (
          <ActionContentContainer>{actionContent}</ActionContentContainer>
        )}
        {customContent}
      </Wrapper>
    );
  },
);

BaseInput.displayName = 'BaseInput';
